import React, { useState, useEffect, useRef, useCallback } from "react";
import InputFieldwithSearch from "../inputfields/InputFieldwithSearch";
import DropdownItem from "./DropdownItem";
import { AnimatePresence, motion } from "framer-motion";
import { getFirefoxCssFix } from "../commonfunctions/getFirefoxCssFix";
import DropdownFramer from "../framerMotion/DropdownFramer";
import Image from "../images/Image";
import DropDownModel from "./DropDownModel";
import useOutsideClick from "../CustomHooks/useOutsideClick";
import ErrorMessage from "../ErrorMessages/ErrorMessage";

// Types for the nested dropdown item
interface NestedDropdownItem {
  label: string;
  value: string;
  isNested?: boolean;
  apiEndpoint?: string;
  id?: string;
}

// Types for user data in nested dropdown
interface UserData {
  user_name: string;
  user_code: string;
  status: string;
  [key: string]: any;
}

interface NestedDropdownProps {
  options: NestedDropdownItem[];
  onSelect?: (value: any, action?: boolean) => void;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  dropdownTopClass?: string;
  testId?: string;
  selectedData?: any;
  errorMessage?: string;
  fetchUserData?: (endpoint: string, searchTerm: string, page?: number) => Promise<{
    data: {
      users: UserData[];
      total_count: number;
    }
  }>;
}

const NestedDropdown: React.FC<NestedDropdownProps> = ({
  options,
  onSelect,
  id,
  className,
  style,
  dropdownTopClass,
  testId,
  selectedData,
  errorMessage,
  fetchUserData = async () => ({ data: { users: [], total_count: 0 } })
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(selectedData || null);
  const [nestedDropdownData, setNestedDropdownData] = useState<UserData[]>([]);
  const [nestedIsOpen, setNestedIsOpen] = useState(false);
  const [nestedPosition, setNestedPosition] = useState<"right" | "left">("right");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeNestedOption, setActiveNestedOption] = useState<NestedDropdownItem | null>(null);
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const buttonRef = useRef<HTMLDivElement>(null);
  const [buttonWidth, setButtonWidth] = useState(0);

  const mainDropdownRef = useRef<HTMLDivElement>(null);
  const nestedDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownItemRef = useRef<HTMLDivElement>(null);

  const useDebounce = (callback: Function, delay: number) => {
    const timeoutRef = useRef<NodeJS.Timeout>();

    return useCallback((...args: any[]) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    }, [callback, delay]);
  };

  // Add the debounced search function
  const debouncedSearch = useDebounce(async (searchValue: string) => {
    if (activeNestedOption?.apiEndpoint) {
      setLoading(true);
      try {
        const response = await fetchUserData(activeNestedOption.apiEndpoint, searchValue, 1);
        setNestedDropdownData(response.data.users);
        setHasMoreData(response.data.total_count > response.data.users.length);
        setCurrentPage(1);
      } catch (error) {
        console.error("Error searching nested dropdown data:", error);
      } finally {
        setLoading(false);
      }
    }
  }, 500); // 500ms delay

  // Update the handleSearch function
  const handleSearch = (value: string) => {
    // Prevent empty spaces only
    if (!value.trim() && value !== "") {
      return;
    }

    // Update internal search term immediately for UI
    setInternalSearchTerm(value);

    // Trim the value and update the actual search term
    const trimmedValue = value.trim();
    setSearchTerm(trimmedValue);

    // Trigger the debounced search
    debouncedSearch(trimmedValue);
  };


  useEffect(() => {
    if (selectedData) {
      // If no exact match is found, just use the selectedData as is
      setSelectedOption(selectedData);
    }
  }, [selectedData]);

  // Determine the position of the nested dropdown
  useEffect(() => {
    if (dropdownItemRef.current && mainDropdownRef.current) {
      const rect = dropdownItemRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // If the right edge of the dropdown would go beyond the viewport width,
      // position it to the left instead
      if (rect.right + 300 > viewportWidth) {
        setNestedPosition("left");
      } else {
        setNestedPosition("right");
      }
    }
  }, [nestedIsOpen]);

  // Load nested dropdown data when opening
  const loadNestedData = useCallback(async (option: NestedDropdownItem) => {
    if (!option.apiEndpoint) return;

    setLoading(true);
    try {
      const response = await fetchUserData(option.apiEndpoint, searchTerm, 1);
      setNestedDropdownData(response.data.users);
      setHasMoreData(response.data.total_count > response.data.users.length);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching nested dropdown data:", error);
      setNestedDropdownData([]);
    } finally {
      setLoading(false);
    }
  }, [fetchUserData, searchTerm]);

  // Handle main dropdown option click
  const handleOptionClick = async (option: NestedDropdownItem) => {
    if (option.isNested) {
      setActiveNestedOption(option);
      setActiveDropdownId(option.id || null);
      await loadNestedData(option);
      setNestedIsOpen(true);
    } else {
      setSelectedOption(option.label);
      onSelect && onSelect(option.value, false);
      setIsOpen(false);
      setNestedIsOpen(false);
      setActiveDropdownId(null);
    }
  };

  // Handle nested dropdown item selection
  const handleNestedItemSelect = (userData: UserData) => {
    onSelect && onSelect(userData, false);
    setNestedIsOpen(false);
    setIsOpen(false);
    setActiveDropdownId(null);
    setSelectedOption(`${userData.user_name} (${userData.user_code})`);
  };

  // Handle infinite scroll in nested dropdown
  // Handle infinite scroll in nested dropdown
  const handleScroll = async (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;

    // Calculate scroll position and threshold
    const scrollPosition = Math.ceil(target.scrollTop + target.clientHeight);
    const threshold = target.scrollHeight - 20; // 20px before the end

    if (
      scrollPosition >= threshold &&
      activeNestedOption?.apiEndpoint &&
      hasMoreData &&
      !loading
    ) {
      setLoading(true);
      const nextPage = currentPage + 1;

      try {
        const response = await fetchUserData(
          activeNestedOption.apiEndpoint,
          searchTerm,
          nextPage
        );

        if (response.data.users.length > 0) {
          setNestedDropdownData(prev => [...prev, ...response.data.users]);
          setCurrentPage(nextPage);
          setHasMoreData(
            response.data.total_count > (nestedDropdownData.length + response.data.users.length)
          );
        } else {
          setHasMoreData(false);
        }
      } catch (error) {
        console.error("Error loading more data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = (action: boolean) => {
    // if (action === isOpen) return; // Prevent unnecessary state updates
    setIsOpen(action);
    setNestedIsOpen(false);
    setActiveDropdownId(null);
    setInternalSearchTerm("");
    handleSearch("");
  };

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, [isOpen]);

  return (
    <DropDownModel isOpen={isOpen} handleClose={handleClose} modalStyle={className}>
      <div className="relative" data-testid={testId}>
        {/* Main dropdown trigger button */}
        <div
          ref={buttonRef}
          className={`flex items-center justify-between px-4 py-3 w-full ${selectedOption ? 'text-black' : 'text-gray-400'} h-14 text-subTitle ${isOpen ? 'border-2' : 'border'} ${errorMessage ? 'border-red-500' : 'border-P_light_active'}  rounded-lg cursor-pointer`}
          onClick={() => handleClose(!isOpen)}
        >
          <span className="truncate max-w-[460px]">{selectedOption || "Select parent user"}</span>
          <DropdownFramer isDropdownVisible={isOpen}>
            <Image src={"dropdown-down-arrow"} />
          </DropdownFramer>
        </div>
        {errorMessage && (
          <ErrorMessage errorMessage={errorMessage} />
        )}
        {/* Main dropdown */}
        {isOpen && (
          <motion.div
            ref={mainDropdownRef}
            className={`${dropdownTopClass || "top-[57px]"} w-full rounded-lg border border-P_light_active p-3.5 absolute bg-white z-40`}
            style={{ width: buttonWidth }}
          >
            <div
              className={`overflow-y-auto overflow-x-hidden ${getFirefoxCssFix(
                "scroll"
              )} scroll-auto h-fit p-1`}
            >
              {options.map((option, index) => (
                <div
                  key={index}
                  ref={option.isNested ? dropdownItemRef : undefined}
                  className="flex items-center justify-between p-2.5 text-primary_text text-subTitle hover:bg-information_light hover:rounded-lg cursor-pointer"
                  onClick={() => handleOptionClick(option)}
                  data-testid ={option.label}
                >
                  <span>{option.label}</span>
                  {option.isNested && (
                    <DropdownFramer
                      isDropdownVisible={activeDropdownId === option.id}
                      nestedDropdown={true}
                    >
                      <Image src={"dropdown-down-arrow"} />
                    </DropdownFramer>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Nested dropdown */}
        {nestedIsOpen && activeNestedOption && (
          <motion.div
            ref={nestedDropdownRef}
            className={`rounded-lg border border-P_light_active p-3.5 mt-[1px] absolute bg-white z-50 w-full`}
            style={{ width: buttonWidth }}
          >
            {/* Nested dropdown header */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">
                {activeNestedOption.label}
              </h3>
              <button
                onClick={() => {
                  setNestedIsOpen(false)
                  setInternalSearchTerm("")
                  handleSearch('')
                  setActiveDropdownId(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            {/* Search input */}
            <div className="mb-3">
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2 pr-10 text-sm border border-P_light_active rounded-md focus:outline-none focus:ring-p_primary focus:border-p_primary"
                  placeholder="Search"
                  value={internalSearchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Nested dropdown list */}
            <div
              className={`overflow-y-auto ${getFirefoxCssFix(
                "scroll"
              )} scroll-auto max-h-60`}
              onScroll={handleScroll}
            >
              {loading && nestedDropdownData.length === 0 ? (
                // Shimmer loading effect
                <div className="space-y-3 animate-pulse">
                  {[...Array(5)].map((_, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="h-5 bg-gray-200 rounded w-32"></div>
                      <div className="h-5 bg-gray-200 rounded w-20"></div>
                      <div className="h-5 bg-gray-200 rounded w-16"></div>
                    </div>
                  ))}
                </div>
              ) : nestedDropdownData.length === 0 ? (
                <p className="py-2 text-sm text-gray-500">No data found.</p>
              ) : (
                nestedDropdownData.map((user, index) => (

                  <div
                    key={index}
                    className="flex items-center justify-between p-2 mb-1 mr-1 text-sm cursor-pointer hover:bg-information_light hover:rounded-lg"
                    onClick={() => handleNestedItemSelect(user)}
                    data-testid={`${user.user_code}`}
                  >
                    <div className="flex flex-row items-center gap-2 w-full">
                      <span className="font-medium break-all w-1/3">
                        {user.user_name}
                      </span>
                      <span className="text-xs text-gray-500 w-1/3">
                        {user.user_code}
                      </span>
                      <span
                        className={`px-2 py-1 text-center text-p_primary text-xs h-fit rounded-full w-1/3 bg-p_light`}
                      >
                        {user.status}
                      </span>
                    </div>
                  </div>
                ))
              )}

              {/* Loading indicator at the bottom for infinite scroll */}
              {loading && nestedDropdownData.length > 0 && (
                <div className="flex justify-center py-2">
                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full animate-spin border-t-p_primary"></div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </DropDownModel>
  );
};

export default NestedDropdown;